/**
 * i18n del timeline de Historial (OpenSpec 008, Fase 3).
 *
 * La frase se guarda POR PARTES en inglés (gramática de la planilla). Acá se
 * traduce al vuelo según el locale: verbos (taxonomía cerrada) + conectores +
 * los subjects/targets/estados ESTRUCTURALES conocidos. Los datos libres (nombre
 * de producto, dirección, montos, detalle) se dejan tal cual — son datos, no texto.
 *
 * El artículo `the` es gendered en español: se resuelve con el género del
 * sustantivo que sigue (mapa NOUN_ES). Términos desconocidos caen al original.
 */
import type { HistoryRow } from "@/services/history.api";

/**
 * Los verbos en inglés. Vivían en `history.api.ts`, que es el cliente HTTP: son i18n y su
 * único consumidor es este archivo, al lado de su gemelo en español. Traerlos acá deja este
 * módulo sin dependencias de runtime —el tipo se borra al compilar—, que es lo que lo hace
 * testeable sin levantar el cliente.
 */
const VERB_LABELS_EN: Record<string, string> = {
  created: "Created",
  requested: "Requested",
  sent: "Sent",
  added: "Added",
  modified: "Modified",
  approved: "Approved",
  cancelled: "Cancelled",
  changed_status: "Changed status",
  restored: "Restored",
  rejected: "Rejected",
  archived: "Archived",
  removed: "Removed",
  viewed: "Viewed",
  merged: "Merged",
  shared: "Shared",
  linked: "Linked",
  unlinked: "Unlinked",
};

const VERB_ES: Record<string, string> = {
  created: "Creó",
  requested: "Solicitó",
  sent: "Envió",
  added: "Agregó",
  modified: "Modificó",
  approved: "Aprobó",
  cancelled: "Canceló",
  changed_status: "Cambió de estado",
  restored: "Restauró",
  rejected: "Rechazó",
  archived: "Archivó",
  removed: "Removió",
  viewed: "Visualizó",
  // OpenSpec 021: la unificación de contactos. Sin esta entrada el historial mostraba el verbo
  // crudo (`merged`).
  merged: "Unificó",
  // Change `mensaje-con-link-al-presupuesto`: un mensaje con el link del presupuesto se registra en
  // el historial de ese presupuesto como "Compartió el Presupuesto #N a <contacto>".
  shared: "Compartió",
  // Productos asociados (xcons-docs#611): el vínculo de un producto del seller con el catálogo.
  linked: "Asoció",
  unlinked: "Desasoció",
};

type Gender = "m" | "f";

/** Sustantivos estructurales → [ES, género] (el género define el artículo `the`). */
const NOUN_ES: Record<string, [string, Gender]> = {
  Quote: ["Presupuesto", "m"],
  "Work name": ["nombre de obra", "m"],
  Order: ["Pedido", "m"],
  "Order invoice": ["Factura del pedido", "f"],
  "Order credit memo": ["Nota de crédito", "f"],
  "credit memo": ["nota de crédito", "f"],
  Contact: ["Contacto", "m"],
  Group: ["Grupo", "m"],
  // Productos asociados (xcons-docs#611): "Modificó el factor de <producto>", y los campos
  // que cambia una importación hecha por una persona (Excel o la edición del asociador).
  factor: ["factor", "m"],
  price: ["precio", "m"],
  "special price": ["precio especial", "m"],
  "cost price": ["costo", "m"],
  stock: ["stock", "m"],
  "lead time": ["reposición", "f"],
  Payment: ["Pago", "m"],
  payment: ["pago", "m"],
  // Sub-permisos del pedido (`order_payment` / `order_shipment`) — los switches del panel
  // de permisos. El backend los manda como subject de la frase: "Modified Payments to Editor".
  Payments: ["Pagos", "m"],
  Deliveries: ["Entregas", "f"],
  "Payment method": ["Método de pago", "m"],
  "Delivery method": ["Método de entrega", "m"],
  // Settings de vendedor/tienda (eventos de config).
  "Shipping method": ["Método de envío", "m"],
  "Shipping rate": ["Tarifa de envío", "f"],
  "Delivery config": ["Configuración de entrega", "f"],
  "Payment config": ["Configuración de pago", "f"],
  "Shipping config": ["Configuración de envío", "f"],
  "Linked seller": ["Seller vinculado", "m"],
  "Delivery address": ["Dirección de entrega", "f"],
  "Billing address": ["Dirección de facturación", "f"],
  Refund: ["Reembolso", "m"],
  "PDF attachment": ["PDF adjunto", "m"],
  "packing slip": ["remito", "m"],
  "payment receipt": ["comprobante de pago", "m"],
  "order label": ["etiqueta de envío", "f"],
  tracking: ["seguimiento", "m"],
  Rating: ["Calificación", "f"],
  Conversation: ["Conversación", "f"],
  // Catálogo (PIM). El producto es el cuarto módulo del historial: sus subjects son el
  // producto, y las dos formas en que un cambio puede estar sin publicar.
  product: ["producto", "m"],
  draft: ["borrador", "m"],
  "pending version": ["versión pendiente", "f"],
  owner: ["dueño", "m"],
  // El resto del Catálogo (xcons-docs#436): atributos y sus opciones, conjuntos y sus grupos,
  // categorías, las concesiones de nivel y las corridas del importador. En minúscula, como el
  // producto: van en mitad de la frase. `group` no pisa a `Group` (el grupo de contactos),
  // que es otra clave. El código del atributo, el nombre del grupo o de la opción y el número
  // de la corrida son `target`/`detail`: dato libre, no se traducen.
  attribute: ["atributo", "m"],
  option: ["opción", "f"],
  "attribute set": ["conjunto de atributos", "m"],
  group: ["grupo", "m"],
  category: ["categoría", "f"],
  "catalog permission": ["permiso del Catálogo", "m"],
  import: ["importación", "f"],
  note: ["nota", "f"],
  Coversation: ["Conversación", "f"],
  Participant: ["Participante", "m"],
};

/** Estados (subject de "Changed status" y afines). */
const STATUS_ES: Record<string, string> = {
  "Payment review": "Pago en revisión",
  "Payment verified": "Pago verificado",
  "Pending delivery": "Entrega pendiente",
  "Pending payment": "Pago pendiente",
  Preparing: "Preparando",
  "Ready to deliver": "Listo para entregar",
  "In transit": "En tránsito",
  Delivered: "Entregado",
  Cancelled: "Cancelado",
  "Invoiced - Approved": "Facturado - Aprobado",
  // Estados de la Factura del pedido tal como los manda el backend: el código crudo
  // capitalizado ("Changed status to Invoiced of Order invoice"), no la etiqueta de arriba.
  // Se traducen igual que la etiqueta del estado en la tarjeta de la factura.
  Pending: "Pago pendiente",
  Review: "Pago en revisión",
  Verified: "Pago verificado",
  Invoiced: "Facturado - Aprobado",
  Canceled: "Cancelado",
  Following: "Siguiendo",
  Open: "Abierto",
  Closed: "Cerrado",
  "Bought on": "Comprado",
  Active: "Activo",
  Paused: "Pausado",
  // Estados del producto (PIM Fase 9). Se llaman como en la barra de estado y no como el
  // valor crudo de `vv_state`: `active` es "Publicado", no "Activo". `Paused` ya está arriba
  // y significa lo mismo.
  Draft: "Borrador",
  "In review": "En revisión",
  Published: "Publicado",
  Archived: "Archivado",
};

// `with` lo emite la unificación de contactos (OpenSpec 021). Sin la entrada, la frase quedaba
// mezclada: "Unificó el Contacto with 543518541022".
const CONN_ES: Record<string, string> = {
  to: "a",
  of: "de",
  from: "de",
  as: "como",
  in: "en",
  with: "con",
};

/** `a` (artículo indefinido en inglés) se deja pasar tal cual: en español el sustantivo va sin él. */
function translateNoun(s?: string | null): { text: string; gender?: Gender } | null {
  if (!s || !String(s).trim()) return null;
  const key = String(s).trim();
  if (NOUN_ES[key]) return { text: NOUN_ES[key][0], gender: NOUN_ES[key][1] };
  if (STATUS_ES[key]) return { text: STATUS_ES[key] };
  const d = /^Delivery (\d+)$/.exec(key);
  if (d) return { text: `Entrega ${d[1]}` };
  // `Quote #322367` → `Presupuesto #322367`. El número viaja pegado al sustantivo porque la frase
  // no tiene ranura entre el subject y el destinatario, y el `detail` se renderiza al final.
  const q = /^Quote #(\d+)$/.exec(key);
  if (q) return { text: `Presupuesto #${q[1]}`, gender: "m" };
  return { text: key }; // dato libre (producto, dirección, monto…)
}

/**
 * Si el `target` de la frase es una persona —el contacto del registro— y no una cosa.
 *
 * Una persona es el destinatario de un envío ("WhatsApp **a** Roberto", "Compartió el
 * Presupuesto #N **a** Roberto"): va con `to` y es dato libre, no un sustantivo de las tablas.
 * Todo lo demás es una cosa del propio registro —"de Entrega 1", "de la Factura del pedido", "a
 * Entrega 2"— y no puede linkear a la ficha del contacto (xcons-docs#583).
 */
export function targetIsPerson(conn2?: string | null, target?: string | null): boolean {
  if (conn2 !== "to" || !target || !String(target).trim()) return false;
  const key = String(target).trim();
  if (NOUN_ES[key] || STATUS_ES[key]) return false;
  return !/^(Delivery|Quote #)\s*\d+$/.test(key);
}

/**
 * Devuelve el verbo (para bold) + el resto de la frase, en el locale dado.
 *
 * **Conversaciones no imprime el verbo** (022, decisión #10): la frase de un mensaje es
 * "WhatsApp a Roberto ‹texto›", no "Envió a WhatsApp text message a Roberto ‹texto›". El verbo se
 * mantiene en el dato —`verb` es NOT NULL con una taxonomía cerrada, y el buscador lo usa— pero acá
 * no se renderiza, y el bold pasa al subject, que ocupa su lugar visual: sin eso la columna pierde
 * el anclaje que la alinea con las filas de Presupuesto y Pedido.
 */
export function phraseParts(
  row: HistoryRow,
  lang: string,
): { verb: string; rest: string; target?: string } {
  const es = (lang || "").toLowerCase().startsWith("es");
  const p = row.payload;

  if (row.kind === "note") {
    return { verb: es ? "Nota" : "Note", rest: p.content ?? "" };
  }

  const hideVerb = p.module === "conversation" && p.verb === "sent";

  if (!es) {
    if (hideVerb) {
      const rest = [p.conn2, p.target, p.detail]
        .filter((x) => x && String(x).trim())
        .join(" ");
      return { verb: p.subject ?? "", rest, target: p.target ?? undefined };
    }
    const rest = [p.conn1, p.subject, p.conn2, p.target, p.detail]
      .filter((x) => x && String(x).trim())
      .join(" ");
    return { verb: VERB_LABELS_EN[p.verb] ?? p.verb, rest, target: p.target ?? undefined };
  }

  const subj = translateNoun(p.subject);
  const tgt = translateNoun(p.target);
  const conn = (c?: string | null, gender?: Gender): string => {
    if (!c || !String(c).trim()) return "";
    if (c === "the") return gender === "f" ? "la" : "el";
    return CONN_ES[c] ?? c;
  };
  if (hideVerb) {
    const rest = [conn(p.conn2, tgt?.gender), tgt?.text, p.detail]
      .filter((x) => x && String(x).trim())
      .join(" ");
    // `WhatsApp`/`WhatsApp image` no se traducen: son el nombre del canal, no vocabulario.
    return { verb: subj?.text ?? "", rest, target: tgt?.text };
  }

  const rest = [
    conn(p.conn1, subj?.gender ?? tgt?.gender),
    subj?.text,
    conn(p.conn2, tgt?.gender),
    tgt?.text,
    // Dato libre, salvo el origen de una asociación de Productos asociados: "(suggestion 99%)".
    p.detail?.replace(/\bsuggestion\b/, "sugerencia"),
  ]
    .filter((x) => x && String(x).trim())
    .join(" ");
  return { verb: VERB_ES[p.verb] ?? p.verb, rest, target: tgt?.text };
}

/**
 * Caso A: label del actor Guest (usuario no registrado operando desde el Store). El backend
 * graba actorType='guest' con actor_name='Guest'; acá se traduce según locale. El texto ES es
 * "Usuario invitado" por spec (#159, Historial.md → "Generador: usuario invitado"). Cuando ese
 * invitado se registra, el backend reasigna sus logs a su nombre real (actorType pasa a
 * 'contact') y este label deja de aplicar.
 */
export function guestLabel(lang: string): string {
  return (lang || "").toLowerCase().startsWith("es") ? "Usuario invitado" : "Guest user";
}
